'use client';

import { type ReactNode, useMemo, useState } from 'react';
import { useTranslations } from 'next-intl';
import { useSearchParams } from 'next/navigation';
import { usePathname, useRouter } from '@/src/i18n/navigation';

import { css } from '@/styled-system/css';
import { Banner, Menu, Text, TextButton } from '@zillow/constellation-10';
import { ArrowUpDown, Check } from 'lucide-react';
import { LucideIcon } from '@/src/components/LucideIcon';
import { useFilters } from '@/src/lib/filters';
import { buildSortCriteriaUrl } from './srpCriteriaUrl';
import { emitTrackSortListingsEvent } from '@/src/analytics/hotpads/events';
import { SrpSaveSearchButton } from './SrpSaveSearchButton';

type Props = {
  pageTitle: string;
  isViewingDetectedArea: boolean;
  /** Streamed count line (client component that tracks live listings query). */
  countsSlot: ReactNode;
};

const SORT_OPTIONS: Array<{
  value: string;
  labelKey: 'sortRecommended' | 'sortNewest' | 'sortMostPopular' | 'sortHighPrice' | 'sortLowPrice';
}> = [
  { value: 'recommended', labelKey: 'sortRecommended' },
  { value: 'activated', labelKey: 'sortNewest' },
  { value: 'weekViews', labelKey: 'sortMostPopular' },
  { value: 'highPrice', labelKey: 'sortHighPrice' },
  { value: 'lowPrice', labelKey: 'sortLowPrice' },
];

const actionsNavRoot = css({
  display: 'flex',
  flexDirection: 'column',
  background: 'bg.neutral',
  paddingBottom: 'tight',
  width: '100%',
  position: 'relative',
});

const summaryAndActionsRow = css({
  display: 'flex',
  flexDirection: 'column',
  gap: 'tighter',
  width: '100%',
  minWidth: 0,
  lg: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 'default',
  },
});

const summaryBlock = css({
  display: 'flex',
  flexDirection: 'column',
  flex: '1 1 0',
  minWidth: 0,
});

const countContainer = css({
  minWidth: 0,
});

const metadataActionControls = css({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 'default',
  width: '100%',
  flexShrink: 0,
  lg: {
    justifyContent: 'flex-end',
    width: 'auto',
    marginLeft: 'auto',
  },
});

const actionControlItem = css({
  display: 'flex',
  alignItems: 'center',
  flexShrink: 0,
});

export function ActionsNavBarClient({ pageTitle, isViewingDetectedArea, countsSlot }: Props) {
  const t = useTranslations('search');
  const sortOptions = useMemo(() => SORT_OPTIONS.map((opt) => ({ value: opt.value, label: t(opt.labelKey) })), [t]);
  const [isSortOpen, setIsSortOpen] = useState(false);
  const { filters, setFilters } = useFilters();
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  const sortOrder = filters.orderBy ?? 'recommended';
  const sortLabel = sortOptions.find((o) => o.value === sortOrder)?.label ?? t('sortRecommended');

  const handleSortChange = (value: string) => {
    if (value !== sortOrder) {
      emitTrackSortListingsEvent({ triggerLocation: 'search_results', triggerObject: 'search_results|header' });
    }
    setFilters({ ...filters, orderBy: value === 'recommended' ? null : value });
    router.replace(buildSortCriteriaUrl(pathname, new URLSearchParams(searchParams?.toString() ?? ''), value), {
      scroll: false,
    });
    setIsSortOpen(false);
  };

  const sortControl = (
    <Menu
      placement="bottom-start"
      open={isSortOpen}
      onOpenChange={setIsSortOpen}
      trigger={
        <TextButton.Root
          type="button"
          tone="neutral"
          textStyle="body-sm-bold"
          aria-label={`${t('sortPrefix')} ${sortLabel}`}
          data-testid="button-sort"
        >
          <TextButton.Icon size="sm">
            <LucideIcon icon={ArrowUpDown} size="sm" />
          </TextButton.Icon>
          <TextButton.Label>{sortLabel}</TextButton.Label>
        </TextButton.Root>
      }
      content={
        <div data-testid="sort-dropdown">
          {sortOptions.map((opt) => {
            const selected = opt.value === sortOrder;
            return (
              <Menu.Item
                key={opt.value}
                onClick={() => handleSortChange(opt.value)}
                data-testid={`sort-option-${opt.value}`}
              >
                {selected && (
                  <Menu.ItemIcon>
                    <LucideIcon icon={Check} size="sm" />
                  </Menu.ItemIcon>
                )}
                <Menu.ItemLabel>{opt.label}</Menu.ItemLabel>
              </Menu.Item>
            );
          })}
        </div>
      }
    />
  );

  return (
    <div id="srp-actions-nav" data-srp-component="actions-nav" data-testid="srp-navbar" className={actionsNavRoot}>
      <div className={summaryAndActionsRow}>
        <div data-srp-component="summary" className={summaryBlock}>
          <Text asChild textStyle="body-lg-bold">
            <h1 className={css({ margin: 0 })}>{pageTitle}</h1>
          </Text>
          <div id="srp-listings-count" data-srp-component="listings-count" className={countContainer}>
            {countsSlot}
          </div>
        </div>

        <div data-srp-component="metadata-actions" className={metadataActionControls}>
          <div id="srp-sort-control" data-srp-component="sort-control" className={actionControlItem}>
            {sortControl}
          </div>
          <div id="srp-save-search-control" data-srp-component="save-search-control" className={actionControlItem}>
            <SrpSaveSearchButton />
          </div>
        </div>
      </div>

      {isViewingDetectedArea && (
        <Banner tone="info" css={{ display: 'inline-flex' }}>
          {t('viewingDetectedAreaAlert')}
        </Banner>
      )}
    </div>
  );
}
